// Introducer Portal — Concierge restyle (2026-06-25).
//
// Wraps existing deal-list + checklist behaviour in the new <PortalShell>
// so this portal matches the Client and Partner portals: real horizontal
// Solara lockup, spiral icon watermark, cream bg, navy/gold KPI tiles.
//
// Behaviour preserved 1:1:
//   - GET /api/introducer/dashboard for the list
//   - GET /api/introducer/upline for the partner upline strip
//   - GET /api/introducer/deals/{id}/checklist when expanding a deal
//   - useNoIndex() so the portal stays out of Google
//
// New styling:
//   - 4 KpiTile tiles (deals / your share / paid / outstanding)
//   - Upline strip + SolaraContactCard side by side
//   - Deal cards in rounded-xl + gold border + serif company name
//   - Expanded checklist preserved with the same status pills + items

import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ChevronDown, ChevronUp, FileText, CheckCircle, Clock,
  XCircle, MinusCircle, Mail, Users, LogOut, Settings,
} from "lucide-react";

import { useAuth } from "@/context/AuthContext";
import { formatCurrency as fmtCurrency } from "@/lib/formatters";
import { useNoIndex } from "@/lib/useNoIndex";
import { DEAL_SUBSTAGES } from "@/lib/dealStages";
import PortalShell, {
  PortalGreeting,
  KpiStrip,
  KpiTile,
  SolaraContactCard,
  PortalBody,
} from "@/components/portal/PortalShell";
// PR Portal-V2-Partner-Introducer-Messaging (2026-06-26) — per-deal
// messaging hub mirroring the partner portal addition.
import PortalMessagingHub from "@/components/portal/PortalMessagingHub";
// TASK-2 (2026-07-10) — tasks assigned to this introducer by Solara.
import PortalTasksCard from "@/components/portal/PortalTasksCard";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_META = {
  pending:      { label: "Outstanding",    icon: Clock,        classes: "bg-amber-100 text-amber-700" },
  uploaded:     { label: "Awaiting review", icon: Clock,        classes: "bg-blue-100 text-blue-700" },
  accepted:     { label: "Approved",       icon: CheckCircle,  classes: "bg-green-100 text-green-700" },
  rejected:     { label: "Rejected",       icon: XCircle,      classes: "bg-red-100 text-red-700" },
  not_required: { label: "Not required",   icon: MinusCircle,  classes: "bg-gray-100 text-gray-500" },
};

const DEAL_STATUS_LABELS = {
  draft: "Draft",
  submitted: "Submitted",
  under_review: "Under Review",
  application_completed: "Application Completed",
  assessment: "In Assessment",
  submitted_to_lender: "With Lender",
  indicative_terms: "Indicative Terms Issued",
  conditions_precedent: "Conditions Precedent",
  mortgage_docs_issued: "Mortgage Docs Issued",
  settlement: "Settlement",
  complete: "Settled",
  approved: "Approved",
  rejected: "Rejected",
  ...DEAL_SUBSTAGES.reduce((acc, s) => { acc[s.id] = s.label; return acc; }, {}),
};

export default function IntroducerPortal() {
  useNoIndex();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [upline, setUpline] = useState(null);
  const [expandedDealId, setExpandedDealId] = useState(null);
  const [checklistByDeal, setChecklistByDeal] = useState({});
  const [loadingChecklist, setLoadingChecklist] = useState(null);

  useEffect(() => {
    loadDashboard();
    loadUpline();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadDashboard = async () => {
    try {
      const res = await axios.get(`${API}/introducer/dashboard`);
      setData(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to load dashboard");
    } finally {
      setLoading(false);
    }
  };

  const loadUpline = async () => {
    try {
      const res = await axios.get(`${API}/introducer/upline`);
      setUpline(res.data?.partner || null);
    } catch (_) {
      setUpline(null);
    }
  };

  const expandDeal = async (dealId) => {
    if (expandedDealId === dealId) {
      setExpandedDealId(null);
      return;
    }
    setExpandedDealId(dealId);
    if (!checklistByDeal[dealId]) {
      setLoadingChecklist(dealId);
      try {
        const res = await axios.get(`${API}/introducer/deals/${dealId}/checklist`);
        setChecklistByDeal((prev) => ({ ...prev, [dealId]: res.data }));
      } catch (error) {
        toast.error(error.response?.data?.detail || "Failed to load checklist");
      } finally {
        setLoadingChecklist(null);
      }
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const fmt = {
    currency: (v) => fmtCurrency(v || 0, 0),
    pct: (v) => `${(v ?? 0).toFixed(v % 1 === 0 ? 0 : 2)}%`,
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f0ede4]">
        <div className="animate-spin w-10 h-10 border-4 border-[#bd9a59] border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!data) return null;

  const { deals = [], summary = {}, introducer = {} } = data;
  const firstName = (introducer.name || "").split(" ")[0] || introducer.name || "";

  const subtitle = deals.length > 0
    ? `${summary.deal_count ?? deals.length} introduced deal${(summary.deal_count ?? deals.length) === 1 ? "" : "s"} · Your share ${fmt.currency(summary.total_my_share)}${introducer.default_commission_split_pct != null ? ` · ${introducer.default_commission_split_pct}% default split` : ""}`
    : `No introduced deals yet${introducer.default_commission_split_pct != null ? ` · ${introducer.default_commission_split_pct}% default split` : ""}`;

  const rightActions = (
    <>
      <Link
        to="/settings"
        className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs text-white/80 hover:text-white hover:bg-white/10 rounded-md"
        data-testid="introducer-settings"
      >
        <Settings className="w-4 h-4" />
        <span className="hidden md:inline">Settings</span>
      </Link>
      <button
        onClick={handleLogout}
        className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs text-white/80 hover:text-white hover:bg-white/10 rounded-md"
        data-testid="introducer-logout"
      >
        <LogOut className="w-4 h-4" />
        <span className="hidden md:inline">Sign out</span>
      </button>
    </>
  );

  return (
    <PortalShell portalLabel="Introducer Portal" brandHref="/introducer" rightActions={rightActions}>
      <PortalGreeting
        title={firstName ? `Welcome back, ${firstName}` : "Welcome back"}
        subtitle={subtitle}
      />

      <KpiStrip>
        <KpiTile label="Deals" value={summary.deal_count ?? deals.length} />
        <KpiTile label="Your share" value={fmt.currency(summary.total_my_share)} accent />
        <KpiTile label="Paid to you" value={fmt.currency(summary.total_my_share_paid)} />
        <KpiTile label="Outstanding" value={fmt.currency(summary.total_my_share_outstanding)} />
      </KpiStrip>

      {/* Upline strip + Solara contact card side by side */}
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 pt-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {upline ? (
            <UplineCard upline={upline} />
          ) : (
            <SolaraContactCard
              title="Your Solara contact"
              name="Solara Loans Team"
              role="Introducer support"
              email="loans@sgcap.com.au"
            />
          )}
          {upline && (
            <SolaraContactCard
              title="Solara head office"
              name="Solara Loans Team"
              role="Operations & payouts"
              email="loans@sgcap.com.au"
            />
          )}
        </div>
      </div>

      {/* TASK-2 (2026-07-10) — tasks assigned to this introducer by Solara.
          Renders nothing when no tasks exist, keeping the dashboard clean. */}
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 pt-4">
        <PortalTasksCard />
      </div>

      {/* Portal-V2-Partner-Introducer-Messaging (2026-06-26) — per-deal
          messaging hub. Reuses the deals payload from /introducer/dashboard
          so no extra fetch needed. */}
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 pt-4">
        <PortalMessagingHub
          deals={(deals || []).map((d) => ({
            id: d.id,
            application_reference: d.reference || "",
            company_name: d.company || d.client_name || "",
            assigned_broker_name: d.assigned_broker_name || "",
            assigned_broker_email: d.assigned_broker_email || "",
            assigned_broker_phone: d.assigned_broker_phone || "",
          }))}
          title="Message Solara about a deal"
          emptyHint="When Solara links a client to you, you can message about any of their deals here."
          storageKey="introducer"
        />
      </div>

      <PortalBody>
        <h2 className="font-serif text-[12px] uppercase tracking-wider text-[#021d49] mb-3">
          Your introduced deals
        </h2>

        {deals.length === 0 ? (
          <div className="bg-white border border-[#bd9a59]/30 rounded-xl p-10 text-center" data-testid="introducer-no-deals">
            <FileText className="w-10 h-10 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500 text-sm">No deals yet. When Solara links a client to you, their deals will appear here.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {deals.map((deal) => {
              const isExpanded = expandedDealId === deal.id;
              const checklist = deal.checklist || {};
              return (
                <div
                  key={deal.id}
                  className="bg-white border border-[#bd9a59]/30 rounded-xl overflow-hidden"
                  data-testid={`introducer-deal-${deal.id}`}
                >
                  <button
                    type="button"
                    onClick={() => expandDeal(deal.id)}
                    className="w-full text-left px-5 pt-4 pb-3 hover:bg-[#fafaf9] transition-colors"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="min-w-0 flex-1">
                        <div className="font-serif text-lg text-[#021d49] truncate">
                          {deal.company || deal.client_name || "Unnamed"}
                        </div>
                        <div className="text-[11px] font-mono text-gray-500 mt-0.5">
                          {deal.reference}
                        </div>
                      </div>
                      <div className="text-right shrink-0">
                        <div className="font-serif text-xl text-[#021d49] leading-none">
                          {fmt.currency(deal.loan_amount)}
                        </div>
                        <div className="text-[11px] text-gray-500 mt-1">
                          {DEAL_STATUS_LABELS[deal.status] || deal.status?.replace(/_/g, " ")}
                        </div>
                      </div>
                    </div>

                    {/* Chips row */}
                    <div className="flex flex-wrap items-center gap-2 mt-3 text-[12px]">
                      <ChecklistPill checklist={checklist} />
                      <span className="ml-auto flex items-center gap-1 text-[11px] text-gray-500">
                        {isExpanded ? "Hide checklist" : "Open checklist"}
                        {isExpanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                      </span>
                    </div>

                    {/* Mini-stats: split / share / paid / outstanding */}
                    <div className="mt-3 pt-3 border-t border-[#f5f1e7] grid grid-cols-2 sm:grid-cols-4 gap-3 text-[12px]">
                      <Stat label="Split" value={fmt.pct(deal.my_split_pct)} color="text-[#021d49]" />
                      <Stat label="Your share" value={fmt.currency(deal.my_share)} color="text-[#bd9a59]" />
                      <Stat label="Paid to you" value={fmt.currency(deal.my_share_paid)} color="text-green-600" />
                      <Stat label="Outstanding" value={fmt.currency(deal.my_share_outstanding)} color="text-red-600" />
                    </div>
                  </button>

                  {isExpanded && (
                    <div className="border-t border-[#f1f1ec] px-5 py-4 bg-[#fafaf9]">
                      {loadingChecklist === deal.id ? (
                        <div className="flex justify-center py-6">
                          <div className="animate-spin w-5 h-5 border-2 border-[#bd9a59] border-t-transparent rounded-full" />
                        </div>
                      ) : checklistByDeal[deal.id] ? (
                        <ChecklistView data={checklistByDeal[deal.id]} />
                      ) : null}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </PortalBody>
    </PortalShell>
  );
}

// ---------- subcomponents ----------

function UplineCard({ upline }) {
  return (
    <div className="bg-white border border-[#bd9a59]/30 rounded-xl p-4" data-testid="introducer-upline-strip">
      <div className="text-[12px] uppercase tracking-wider text-[#021d49] font-medium mb-3">
        Your partner
      </div>
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-full bg-[#021d49]/10 flex items-center justify-center shrink-0">
          <Users className="w-5 h-5 text-[#bd9a59]" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium text-[#021d49] truncate">{upline.name || upline.email || "Partner"}</div>
          <div className="text-[11px] text-gray-500 truncate">Your upline partner</div>
        </div>
      </div>
      {upline.email && (
        <a
          href={`mailto:${upline.email}`}
          className="mt-3 w-full flex items-center justify-center gap-1.5 px-2 py-2 text-xs rounded-md bg-[#bd9a59] text-[#021d49] hover:bg-[#a8854a]"
          data-testid="introducer-upline-email"
        >
          <Mail className="w-3.5 h-3.5" /> Email partner
        </a>
      )}
    </div>
  );
}

function Stat({ label, value, color }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-gray-500">{label}</div>
      <div className={`text-sm font-semibold ${color}`}>{value}</div>
    </div>
  );
}

function ChecklistPill({ checklist }) {
  const total = checklist.total ?? 0;
  const completed = checklist.accepted ?? 0;
  const pct = checklist.percentage ?? 0;
  const colour = pct >= 100 ? "bg-green-100 text-green-700"
    : pct >= 50 ? "bg-amber-100 text-amber-700"
    : "bg-gray-100 text-gray-600";
  return (
    <span className={`px-2 py-0.5 rounded-full font-medium text-[11px] ${colour}`}>
      Checklist {completed}/{total} ({pct}%)
    </span>
  );
}

function ChecklistView({ data }) {
  const cats = data.doc_categories || [];
  const progress = data.progress || {};
  return (
    <div className="space-y-4" data-testid="introducer-checklist">
      <div className="flex items-center gap-3">
        <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
          <div className="h-full bg-[#bd9a59]" style={{ width: `${progress.percentage || 0}%` }} />
        </div>
        <span className="text-sm font-semibold text-[#021d49]">
          {progress.completed || 0} / {progress.total || 0} approved
        </span>
      </div>

      {cats.length === 0 ? (
        <p className="text-sm text-gray-500">No checklist items yet.</p>
      ) : (
        cats.map((cat) => (
          <div key={cat.name} className="bg-white border border-[#f1f1ec] rounded-md overflow-hidden">
            <div className="px-4 py-2.5 bg-[#fafaf9] border-b border-[#f1f1ec] flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-[#021d49]">{cat.name}</p>
                <p className="text-xs text-gray-500">
                  {cat.completed} of {cat.total} approved · {cat.action_required} outstanding
                </p>
              </div>
            </div>
            <div className="divide-y divide-[#f5f1e7]">
              {(cat.items || []).map((item) => {
                const meta = STATUS_META[item.status] || STATUS_META.pending;
                const Icon = meta.icon;
                return (
                  <div
                    key={item.id}
                    className="px-4 py-2 flex items-center justify-between gap-3 text-sm"
                    data-testid={`introducer-checklist-item-${item.id}`}
                  >
                    <span className="text-gray-700 truncate">{item.name}</span>
                    <span className={`flex items-center gap-1 text-xs px-2 py-1 rounded-full font-medium ${meta.classes}`}>
                      <Icon className="w-3 h-3" /> {meta.label}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        ))
      )}

      <p className="text-[11px] text-gray-400 text-center pt-2 italic">
        Document files are visible only to Solara and the client.
      </p>
    </div>
  );
}
